import * as t from 'io-ts';
import {pick, values} from 'lodash';

import {InboxesFiltersEnum, InboxesSearchLocationsEnum} from '../url/inboxesUrlModel';
import {RAccountsSetupGuideTasksEnum} from './accountsSetupGuideModel';
import {makeApiSerializableType, makeDefaultValueType, makeEnumRuntimeType} from './apiTypes';
import {RCommentReactionError} from './commentReactionModel';
import {RFileCacheItem} from './fileCacheItemModel';
import {RRule} from './ruleModel';
import {RSearchHelpCenterResult} from './searchHelpCenterModel';
import {RSortOption} from './sortOptionModel';
import {RTag} from './tagModel';
import {RVisualRecipient} from './visualRecipientModel';

/*
 * Runtime type.
 */

// Selected filters.
const RSelectedFilters = t.dictionary(
  t.string,
  t.union([
    makeEnumRuntimeType(InboxesFiltersEnum),
    makeEnumRuntimeType(InboxesSearchLocationsEnum),
    t.undefined,
  ]),
);

// Sort options.
const RSortOptions = t.dictionary(t.string, t.union([RSortOption, t.undefined]));

export enum CollapsedItemKeyPrefixesEnum {
  ACCOUNTS_MANAGER = 'accountsManager:',
  COMPANY_CONTACTS = 'companyContacts:',
  CUSTOM_VIEW = 'customView:',
  IMPORT_ACCOUNTS_CSV = 'importAccountsCsv:',
  IMPORT_CONTACTS_CSV = 'importContactsCSV:',
  LISTS = 'lists:',
  MERGE_CONTACTS_BANNER = 'mergeContactsBanner:',
  OTHER_CONTACTS = 'otherContacts:',
  SETTINGS_LOCAL_APPLICATIONS = 'settingsLocalApplications:',
  SETTINGS_AUTOLINKERS = 'settingsAutolinkers:',
  SETTINGS_CIRCLES = 'settingsCircles:',
  SETTINGS_CUSTOM_FIELDS = 'settingsCustomFields:',
  SETTINGS_INBOX_BUSINESS_HOURS = 'settingsInboxBusinessHours:',
  SETTINGS_OAUTH_CLIENT = 'settingsOauthClient:',
  SETTINGS_PREVIEWERS = 'settingsPreviewers:',
  SETTINGS_RULES = 'settingsRules:',
  SETTINGS_SECTION = 'settingsSection:',
  SETTINGS_SERVERS = 'settingsServers:',
  PRIVATE_CONTACTS = 'privateContacts:',
  TEAM_INBOXES = 'teamInboxes:',
  TEAMMATE_INBOX = 'teammateInboxTag:',
  WORKSPACE = 'workspace:',
  P4_APPLICATIONS_MANAGER = 'p4IntegrationsManager:',
}

// Collapsed P1 items.
const RCollapsedItems = t.dictionary(t.string, t.boolean);

// Dismissed channel errors.
const RDismissedChannelErrors = t.dictionary(t.string, t.boolean);

// Dismissed integration errors.
const RDismissedIntegrationErrors = t.dictionary(t.number, t.number);

// Teammate interaction.
const RTeammateInteraction = t.readonly(
  t.type({
    teammateId: t.Integer,
    date: t.Integer,
  }),
);

// Emoji usage.
const REmojiUsage = t.readonly(
  t.type({
    shortcode: t.string,
    date: t.Integer,
  }),
);

// Rule from drag action dialog options.
const RRuleFromDragOptions = t.readonly(t.type({tag: RTag, recipients: t.readonlyArray(RVisualRecipient)}));

// Help center search data storage.
const RHelpCenterSearchCache = t.readonly(t.type({query: t.string, result: RSearchHelpCenterResult}));

// Flags.
export enum LocalPreferencesFlagsEnum {
  SIDE_PANEL = 'SIDE_PANEL_2',
  FORMATTING_TOOLBAR = 'FORMATTING_TOOLBAR',
  ABSOLUTE_DATETIME = 'ABSOLUTE_DATETIME',
  TRACE_PERFORMANCE = 'TRACE_PERFORMANCE',
}
const RLocalPreferencesFlags = makeEnumRuntimeType(LocalPreferencesFlagsEnum);
const RFlags = t.dictionary(RLocalPreferencesFlags, t.boolean);
export type LocalPreferencesFlags = t.TypeOf<typeof RFlags>;

const defaultFlags: LocalPreferencesFlags = {
  [LocalPreferencesFlagsEnum.SIDE_PANEL]: false,
  [LocalPreferencesFlagsEnum.FORMATTING_TOOLBAR]: true,
  [LocalPreferencesFlagsEnum.ABSOLUTE_DATETIME]: false,
  [LocalPreferencesFlagsEnum.TRACE_PERFORMANCE]: false,
};

// Sizes.
export enum LocalPreferencesSizesEnum {
  WORKSPACES = 'WORKSPACES',
  CELLS = 'CELLS',
  SIDE = 'SIDE',
  CALENDARS_POPUP_LEFTPANEL = 'CALENDARS_POPUP_LEFTPANEL',
  CONTACTS_MANAGER_NAVIGATION = 'CONTACTS_MANAGER_NAVIGATION',
  CONTACTS_MANAGER_SIDEPANEL = 'CONTACTS_MANAGER_SIDEPANEL',
}
const RLocalPreferencesSizes = makeEnumRuntimeType(LocalPreferencesSizesEnum);
const RSizes = t.dictionary(RLocalPreferencesSizes, t.Integer);
export type LocalPreferencesSizes = t.TypeOf<typeof RSizes>;

const defaultSizes: LocalPreferencesSizes = {
  [LocalPreferencesSizesEnum.WORKSPACES]: 240,
  [LocalPreferencesSizesEnum.CELLS]: 300,
  [LocalPreferencesSizesEnum.SIDE]: 322,
  [LocalPreferencesSizesEnum.CALENDARS_POPUP_LEFTPANEL]: 240,
  [LocalPreferencesSizesEnum.CONTACTS_MANAGER_NAVIGATION]: 240,
  [LocalPreferencesSizesEnum.CONTACTS_MANAGER_SIDEPANEL]: 400,
};

// P4 Contact Details Panel
export enum ContactDetailsSectionsEnum {
  CONTACT_INFORMATION = 'CONTACT_INFORMATION',
  SUMMARY = 'SUMMARY',
  CONVERSATIONS = 'CONVERSATIONS',
  NOTES = 'NOTES',
}

export enum ContactDetailsTabsEnum {
  CONTACT = 'CONTACT',
  ACCOUNT = 'ACCOUNT',
}

const RContactDetailsSections = makeEnumRuntimeType(ContactDetailsSectionsEnum);
const RContactDetailsTabs = makeEnumRuntimeType(ContactDetailsTabsEnum);
const RContactDetailSectionsExpandState = t.dictionary(RContactDetailsSections, t.boolean);
export type ContactDetailSectionsExpandState = t.TypeOf<typeof RContactDetailSectionsExpandState>;

export const defaultContactDetailSectionsExpandState: ContactDetailSectionsExpandState = {
  [ContactDetailsSectionsEnum.CONTACT_INFORMATION]: true,
  [ContactDetailsSectionsEnum.SUMMARY]: true,
  [ContactDetailsSectionsEnum.CONVERSATIONS]: true,
  [ContactDetailsSectionsEnum.NOTES]: true,
};

const RContactDetailsPreferences = t.readonly(
  t.intersection([
    t.type({
      sectionsExpandState: makeDefaultValueType(
        RContactDetailSectionsExpandState,
        defaultContactDetailSectionsExpandState,
      ),
    }),
    t.partial({
      preferredTab: RContactDetailsTabs,
    }),
  ]),
);

// Analytics Exports Column Selections
const RAnalyticsExportsColumns = t.readonly(
  t.type({
    messagesColumns: t.union([t.readonlyArray(t.string), t.undefined]),
    activitiesColumns: t.union([t.readonlyArray(t.string), t.undefined]),
    csatColumns: t.union([t.readonlyArray(t.string), t.undefined]),
  }),
);

// Account Details
export enum AccountDetailsSectionsEnum {
  ACCOUNT_INFORMATION = 'ACCOUNT_INFORMATION',
  CONTACTS = 'CONTACTS',
  CONVERSATIONS = 'CONVERSATIONS',
}

const RAccountDetailsSections = makeEnumRuntimeType(AccountDetailsSectionsEnum);
const RAccountDetailsSectionsExpandState = t.dictionary(RAccountDetailsSections, t.boolean);
export type AccountDetailsSectionsExpandState = t.TypeOf<typeof RAccountDetailsSectionsExpandState>;

export const defaultAccountDetailsSectionsExpandState: AccountDetailsSectionsExpandState = {
  [AccountDetailsSectionsEnum.ACCOUNT_INFORMATION]: true,
  [AccountDetailsSectionsEnum.CONTACTS]: true,
  [AccountDetailsSectionsEnum.CONVERSATIONS]: true,
};

const RAccountDetailsPreferences = t.readonly(
  t.type({
    sectionsExpandState: makeDefaultValueType(
      RAccountDetailsSectionsExpandState,
      defaultAccountDetailsSectionsExpandState,
    ),
    shouldDisplayImportCSVInfoBanner: t.union([t.boolean, t.undefined]),
  }),
);

// Dismissed default video conferencing banner in calendar
const RDismissedDefaultConferencingBanner = t.record(t.string, t.boolean);

// P4 Multi View information
const RSidepanelMultiViewDetails = t.readonly(
  t.type({
    // Selected frame ids that should be displayed.
    selectedFrameIds: t.readonlyArray(t.union([t.string, t.null])),
    // Holds the frame heights (in percentages) for each frame.
    frameHeights: t.readonlyArray(t.number),
  }),
);

/*
 * Runtime type.
 */

const RLocalPreferencesDirty = t.readonly(
  t.type({
    selectedFilters: RSelectedFilters,
    sortOptions: RSortOptions,
    collapsedItems: RCollapsedItems,
    dismissedChannelErrors: RDismissedChannelErrors,
    dismissedIntegrationErrors: RDismissedIntegrationErrors,
    dismissedIntegrationInformationBanners: makeDefaultValueType(t.readonlyArray(t.string), []),
    requestedIntegrations: makeDefaultValueType(t.readonlyArray(t.string), []),
    teammateInteractions: makeDefaultValueType(t.readonlyArray(RTeammateInteraction), []),
    emojiUsage: makeDefaultValueType(t.readonlyArray(REmojiUsage), []),
    lastSelectedSideFrameId: makeDefaultValueType(t.union([t.undefined, t.string]), undefined),
    selectedSideFrameId: makeDefaultValueType(t.union([t.undefined, t.string]), undefined),
    flags: makeDefaultValueType(RFlags, defaultFlags),
    sizes: makeDefaultValueType(RSizes, defaultSizes),
    unassignedAssignedSwitchCount: makeDefaultValueType(t.Integer, 0),
    ruleFromDragOptions: makeDefaultValueType(t.union([RRuleFromDragOptions, t.undefined]), undefined),
    lastRuleCreated: makeDefaultValueType(t.union([RRule, t.undefined]), undefined),
    lastSuppressionListsRemoval: makeDefaultValueType(t.union([t.string, t.undefined]), undefined),
    commentReactionErrors: makeDefaultValueType(t.readonlyArray(RCommentReactionError), []),
    helpCenterSearchCache: makeDefaultValueType(t.union([RHelpCenterSearchCache, t.undefined]), undefined),
    fileCacheItems: makeDefaultValueType(t.readonlyArray(RFileCacheItem), []),
    analyticsPreferOldUi: makeDefaultValueType(t.union([t.undefined, t.boolean]), undefined),
    contactDetailsPreferences: RContactDetailsPreferences,
    accountDetailsPreferences: RAccountDetailsPreferences,
    dismissedSettingsInfoBoxes: t.dictionary(t.string, t.boolean),
    analyticsExportsColumns: makeDefaultValueType(
      t.union([RAnalyticsExportsColumns, t.undefined]),
      undefined,
    ),
    dismissedDefaultConferencingBanner: RDismissedDefaultConferencingBanner,
    sidepanelMultiViewDetails: RSidepanelMultiViewDetails,
    accountsSetupGuideCompletedTasks: makeDefaultValueType(t.readonlyArray(RAccountsSetupGuideTasksEnum), []),
    enabledLegacyApplicationIds: t.readonlyArray(t.number),
    enabledIntegrationIds: t.readonlyArray(t.number),
  }),
);

/*
 * Type.
 */

export interface SelectedFilters extends t.TypeOf<typeof RSelectedFilters> {}
export interface SortOptions extends t.TypeOf<typeof RSortOptions> {}
export interface CollapsedItems extends t.TypeOf<typeof RCollapsedItems> {}
export interface HelpCenterSearchCache extends t.TypeOf<typeof RHelpCenterSearchCache> {}
export interface LocalPreferencesModel extends t.TypeOf<typeof RLocalPreferencesDirty> {}
export interface ContractDetailsPreferences extends t.TypeOf<typeof RContactDetailsPreferences> {}
export interface AnalyticsExportsColumns extends t.TypeOf<typeof RAnalyticsExportsColumns> {}
export interface AccountDetailsPreferences extends t.TypeOf<typeof RAccountDetailsPreferences> {}
export interface SidepanelMultiViewDetailsModel extends t.TypeOf<typeof RSidepanelMultiViewDetails> {}
export const RLocalPreferences = t.clean<LocalPreferencesModel>(RLocalPreferencesDirty as any);

/*
 * IO methods.
 */

export function parseLocalPreferences(src: any): LocalPreferencesModel {
  // Merge the flags.
  const lastFlags = pick(src.flags, values(LocalPreferencesFlagsEnum));
  const flags = {
    ...defaultFlags,
    ...lastFlags,
  };

  // Merge the sizes.
  const lastSizes = pick(src.sizes, values(LocalPreferencesSizesEnum));
  const sizes = {
    ...defaultSizes,
    ...lastSizes,
  };

  // Parse the contact detail preferences with defaults
  const contactDetailsPreferences = src.contactDetailsPreferences
    ? {
        sectionsExpandState:
          src.contactDetailsPreferences.sectionsExpandState ?? defaultContactDetailSectionsExpandState,
      }
    : {
        sectionsExpandState: defaultContactDetailSectionsExpandState,
      };

  // Merge the other preferences and return.
  return {
    selectedFilters: src.selectedFilters || {},
    sortOptions: src.sortOptions || {},
    collapsedItems: src.collapsedItems || {},
    dismissedChannelErrors: src.dismissedChannelErrors || {},
    dismissedIntegrationErrors: src.dismissedIntegrationErrors || {},
    dismissedIntegrationInformationBanners: src.dismissedIntegrationInformationBanners || [],
    requestedIntegrations: src.requestedIntegrations || [],
    teammateInteractions: src.teammateInteractions || [],
    emojiUsage: src.emojiUsage || [],
    lastSelectedSideFrameId: src.lastSelectedSideFrameId || undefined,
    selectedSideFrameId: src.selectedSideFrameId2 || undefined,
    flags,
    sizes,
    unassignedAssignedSwitchCount: src.unassignedAssignedSwitchCount || 0,
    ruleFromDragOptions: src.ruleFromDragOptions || undefined,
    lastRuleCreated: src.lastRuleCreated || undefined,
    lastSuppressionListsRemoval: src.lastSuppressionListsRemoval || undefined,
    commentReactionErrors: src.commentReactionErrors || [],
    helpCenterSearchCache: src.helpCenterSearchCache || undefined,
    fileCacheItems: src.fileCacheItems || [],
    analyticsPreferOldUi: src.analyticsPreferOldUi || undefined,
    contactDetailsPreferences,
    accountDetailsPreferences: parseAccountDetailsPreferences(src.accountDetailsPreferences),
    dismissedSettingsInfoBoxes: src.dismissedSettingsInfoBoxes || {},
    analyticsExportsColumns: src.analyticsExportsColumns || undefined,
    dismissedDefaultConferencingBanner: src.dismissedDefaultConferencingBanner || {},
    sidepanelMultiViewDetails: parseSidepanelMultiViewDetails(src.sidepanelMultiViewDetails),
    accountsSetupGuideCompletedTasks: src.accountsSetupGuideCompletedTasks || [],
    enabledLegacyApplicationIds: src.enabledLegacyApplicationIds || [],
    enabledIntegrationIds: src.enabledIntegrationIds || [],
  };
}

function parseSidepanelMultiViewDetails(details?: any): SidepanelMultiViewDetailsModel {
  return {
    selectedFrameIds: details?.selectedFrameIds || [],
    frameHeights: details?.frameHeights || [],
  };
}

function parseAccountDetailsPreferences(accountDetailsPreferences: any): AccountDetailsPreferences {
  if (!accountDetailsPreferences) {
    return {
      sectionsExpandState: defaultAccountDetailsSectionsExpandState,
      shouldDisplayImportCSVInfoBanner: true,
    };
  }

  const {sectionsExpandState, shouldDisplayImportCSVInfoBanner} = accountDetailsPreferences;
  return {
    sectionsExpandState: {
      [AccountDetailsSectionsEnum.ACCOUNT_INFORMATION]:
        (sectionsExpandState && sectionsExpandState[AccountDetailsSectionsEnum.ACCOUNT_INFORMATION]) ??
        defaultAccountDetailsSectionsExpandState[AccountDetailsSectionsEnum.ACCOUNT_INFORMATION],
      [AccountDetailsSectionsEnum.CONTACTS]:
        (sectionsExpandState && sectionsExpandState[AccountDetailsSectionsEnum.CONTACTS]) ??
        defaultAccountDetailsSectionsExpandState[AccountDetailsSectionsEnum.CONTACTS],
      [AccountDetailsSectionsEnum.CONVERSATIONS]:
        (sectionsExpandState && sectionsExpandState[AccountDetailsSectionsEnum.CONVERSATIONS]) ??
        defaultAccountDetailsSectionsExpandState[AccountDetailsSectionsEnum.CONVERSATIONS],
    },
    shouldDisplayImportCSVInfoBanner: shouldDisplayImportCSVInfoBanner ?? true,
  };
}

export function serializeLocalPreferences(src: LocalPreferencesModel) {
  return {
    selectedFilters: src.selectedFilters,
    sortOptions: src.sortOptions,
    collapsedItems: src.collapsedItems,
    dismissedChannelErrors: src.dismissedChannelErrors,
    teammateInteractions: src.teammateInteractions,
    emojiUsage: src.emojiUsage,
    lastSelectedSideFrameId: src.lastSelectedSideFrameId,
    selectedSideFrameId2: src.selectedSideFrameId,
    flags: src.flags,
    sizes: src.sizes,
    unassignedAssignedSwitchCount: src.unassignedAssignedSwitchCount,
    ruleFromDragOptions: src.ruleFromDragOptions,
    lastRuleCreated: src.lastRuleCreated,
    lastSuppressionListsRemoval: src.lastSuppressionListsRemoval,
    commentReactionErrors: src.commentReactionErrors,
    helpCenterSearchCache: src.helpCenterSearchCache,
    fileCacheItems: src.fileCacheItems || [],
    analyticsPreferOldUi: src.analyticsPreferOldUi,
    contactDetailsPreferences: src.contactDetailsPreferences,
    accountDetailsPreferences: src.accountDetailsPreferences,
    dismissedSettingsInfoBoxes: src.dismissedSettingsInfoBoxes,
    analyticsExportsColumns: src.analyticsExportsColumns,
    dismissedDefaultConferencingBanner: src.dismissedDefaultConferencingBanner,
    sidepanelMultiViewDetails: src.sidepanelMultiViewDetails,
    accountsSetupGuideCompletedTasks: src.accountsSetupGuideCompletedTasks,
    enabledLegacyApplicationIds: src.enabledLegacyApplicationIds,
    enabledIntegrationIds: src.enabledIntegrationIds,
  };
}

/*
 * API type.
 */

export const ApiLocalPreferences = makeApiSerializableType(
  'localPreferences',
  RLocalPreferences,
  parseLocalPreferences,
  serializeLocalPreferences,
);
